import { StyleSheet, Text, View, Svg, Rect, Path } from "@react-pdf/renderer";
import moment from "moment";

const THEME = {
    primary: "#000000",
    border: "#CBD5E1",
    tableOuterBorder: "#CBD5E1",
    tableHeaderBg: "#F1F5F9",
    subHeaderBg: "#F1F5F9",
    textDark: "#000000",
    textMuted: "#000000",
    white: "#FFFFFF",
};

const CheckboxIcon = ({ checked }) => {
    if (checked) {
        return (
            <Svg width={9} height={9} viewBox="0 0 12 12">
                <Path
                    d="M2.5 6L5 8.5L9.5 3.5"
                    stroke="#000000"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                />
            </Svg>
        );
    }
    return null;
};

const styles = StyleSheet.create({
    mainContainer: {
        width: "100%",
        paddingHorizontal: 14,
        paddingTop: 4,
        paddingBottom: 10,
    },
    hiddenIdText: {
        fontSize: 5,
        color: "white",
        marginBottom: 2,
        textAlign: "right",
    },
    titleContainer: {
        flexDirection: "row",
        alignItems: "center",
        marginBottom: 3,
        marginTop: 6,
    },
    titleBar: {
        width: 2.5,
        height: 10,
        backgroundColor: THEME.primary,
        marginRight: 4,
    },
    titleText: {
        color: THEME.primary,
        fontSize: 6,
        fontFamily: "Helvetica",
    },
    titleLine: {
        flex: 1,
        height: 0.5,
        backgroundColor: THEME.border,
        marginLeft: 6,
        marginTop: 1,
    },
    card: {
        marginBottom: 6,
    },
    roundedContainer: {
        backgroundColor: THEME.white,
        borderWidth: 0.75,
        borderColor: THEME.tableOuterBorder,
        overflow: "hidden",
    },
    subSectionHeader: {
        backgroundColor: THEME.subHeaderBg,
        paddingVertical: 3.5,
        borderBottomWidth: 0.5,
        borderColor: THEME.border,
    },
    subSectionTitle: {
        color: THEME.primary,
        fontSize: 6,
        fontFamily: "Helvetica",
        textAlign: "center",
    },
    particularsRow: {
        flexDirection: "row",
        borderBottomWidth: 0.5,
        borderColor: THEME.border,
        alignItems: "stretch",
        minHeight: 14,
    },
    particularsCellGroup: {
        width: "25%",
        flexDirection: "row",
        borderRightWidth: 0.5,
        borderColor: THEME.border,
        alignItems: "stretch",
    },
    particularsLabelCell: {
        width: "42%",
        backgroundColor: THEME.subHeaderBg,
        paddingVertical: 2.5,
        paddingHorizontal: 3,
        justifyContent: "center",
        borderRightWidth: 0.5,
        borderColor: THEME.border,
    },
    particularsLabelText: {
        fontSize: 6,
        fontFamily: "Helvetica",
        color: THEME.primary,
    },
    particularsValueCell: {
        width: "58%",
        backgroundColor: THEME.white,
        paddingVertical: 2.5,
        paddingHorizontal: 3,
        justifyContent: "center",
    },
    particularsValueText: {
        fontSize: 6,
        fontFamily: "Helvetica",
        color: THEME.textDark,
    },
    rowSplit: {
        flexDirection: "row",
        width: "100%",
        alignItems: "stretch",
    },
    colHalf: {
        flex: 1,
    },
    colThird: {
        flex: 1,
    },
    noBorderRight: {
        borderRightWidth: 0,
    },
    tableRow: {
        flexDirection: "row",
        borderBottomWidth: 0.5,
        borderColor: THEME.border,
        minHeight: 14,
    },
    checkboxCell: {
        flex: 0.25,
        borderRightWidth: 0.5,
        borderColor: THEME.border,
        justifyContent: "center",
        alignItems: "center",
        paddingVertical: 2,
    },
    dataCell: {
        flex: 1,
        borderRightWidth: 0.5,
        borderColor: THEME.border,
        justifyContent: "center",
        alignItems: "center",
        paddingVertical: 2.5,
    },
    headerText: {
        fontSize: 6,
        fontFamily: "Helvetica",
        color: THEME.primary,
        textAlign: "center",
    },
    dataText: {
        fontSize: 6,
        fontFamily: "Helvetica",
        color: THEME.textDark,
        textAlign: "center",
    },
    expenseRow: {
        flexDirection: "row",
        paddingHorizontal: 6,
        borderBottomWidth: 0.5,
        borderColor: THEME.border,
    },
    expenseLabel: {
        width: "35%",
        fontSize: 6,
        color: THEME.textMuted,
        paddingVertical: 2.5,
        borderRightWidth: 0.5,
        borderColor: THEME.border,
        fontFamily: "Helvetica",
    },
    expenseValue: {
        width: "65%",
        fontSize: 6,
        paddingLeft: 5,
        paddingVertical: 2.5,
        color: THEME.textDark,
        fontFamily: "Helvetica",
        textAlign: "left",
    },
});

const SectionTitle = ({ title }) => (
    <View style={styles.titleContainer}>
        <View style={styles.titleBar} />
        <Text style={styles.titleText}>{title}</Text>
        <View style={styles.titleLine} />
    </View>
);

const DataTable = ({ title, headers, keys, data, hasCheckbox = false }) => (
    <View style={styles.roundedContainer}>
        {title && (
            <View style={styles.subSectionHeader}>
                <Text style={styles.subSectionTitle}>{title}</Text>
            </View>
        )}

        {/* Table Header Row */}
        <View style={[styles.tableRow, { backgroundColor: THEME.tableHeaderBg }]}>
            {hasCheckbox && <View style={styles.checkboxCell} />}
            {headers.map((header, index) => (
                <View
                    key={index}
                    style={[
                        styles.dataCell,
                        index === headers.length - 1 && styles.noBorderRight,
                    ]}
                >
                    <Text style={styles.headerText}>{header}</Text>
                </View>
            ))}
        </View>

        {/* Table Data Rows */}
        {data && data.length > 0 ? (
            data.map((row, rowIndex) => (
                <View
                    key={rowIndex}
                    style={[
                        styles.tableRow,
                        rowIndex === data.length - 1 && { borderBottomWidth: 0 },
                    ]}
                >
                    {hasCheckbox && (
                        <View style={styles.checkboxCell}>
                            <CheckboxIcon checked={Boolean(row.Selected)} />
                        </View>
                    )}
                    {keys.map((key, colIndex) => {
                        return (
                            <View
                                key={colIndex}
                                style={[
                                    styles.dataCell,
                                    colIndex === keys.length - 1 && styles.noBorderRight,
                                ]}
                            >
                                <Text style={styles.dataText}>
                                    {row[key] !== undefined && row[key] !== null ? row[key] : "-"}
                                </Text>
                            </View>
                        );
                    })}
                </View>
            ))
        ) : (
            <View style={[styles.tableRow, { borderBottomWidth: 0 }]}>
                <View style={[styles.dataCell, styles.noBorderRight, { flex: 1 }]}>
                    <Text style={styles.dataText}>-</Text>
                </View>
            </View>
        )}
    </View>
);

const MyVesselPdfData = ({ data, pdfId }) => {
    const vesselRows = [
        [
            { label: "Vessel", value: data?.first_section?.vesselName },
            { label: "Port", value: data?.first_section?.port },
            { label: "Class", value: data?.first_section?.Class },
            { label: "Flag", value: data?.first_section?.Flag },
        ],
        [
            { label: "IMO", value: data?.second_section?.IMO || 0 },
            { label: "MMSI", value: data?.first_section?.MMSI || 0 },
            { label: "Built", value: data?.first_section?.built || 0 },
            { label: "DWT", value: data?.first_section?.DWT || 0 },
        ],
        [
            { label: "Draft", value: data?.first_section?.Draft || 0 },
            { label: "LOA", value: data?.first_section?.LOA || 0 },
            { label: "Beam", value: data?.first_section?.Beam || 0 },
            { label: "GRT", value: data?.first_section?.GRT || 0 },
        ],
        [
            { label: "NRT", value: data?.first_section?.NRT || 0 },
            { label: "TPC", value: data?.first_section?.TPC || 0 },
            { label: "Grain", value: data?.first_section?.grain || 0 },
            { label: "Gears", value: data?.first_section?.Gears || 0 },
        ],
        [
            { label: "Grabs", value: data?.first_section?.Grabs || 0 },
            { label: "Scrubber", value: data?.first_section?.scrubber },
            { label: "Light Ship", value: data?.first_section?.light_ship || 0 },
            { label: "Bale Cap", value: data?.first_section?.bale_capacity || 0 },
        ],
    ];

    const ballastConsumptionData = [1, 2, 3, 4].map((num) => ({
        Selected: data?.second_section?.[`BalComb${num}`],
        Speed:
            data?.second_section?.Ballast_Sea_Consumptions?.[`col_${num}`]?.Speed,
        HS: data?.second_section?.Ballast_Sea_Consumptions?.[`col_${num}`]?.HS,
        LS: data?.second_section?.Ballast_Sea_Consumptions?.[`col_${num}`]?.LS,
        MGO: data?.second_section?.Ballast_Sea_Consumptions?.[`col_${num}`]?.MGO,
    }));

    const ladenConsumptionData = [1, 2, 3, 4].map((num) => ({
        Selected: data?.second_section?.[`LadComb${num}`],
        Speed: data?.second_section?.Laden_Sea_Consumptions?.[`col_${num}`]?.Speed,
        HS: data?.second_section?.Laden_Sea_Consumptions?.[`col_${num}`]?.HS,
        LS: data?.second_section?.Laden_Sea_Consumptions?.[`col_${num}`]?.LS,
        MGO: data?.second_section?.Laden_Sea_Consumptions?.[`col_${num}`]?.MGO,
    }));

    const portConsumptionData = ["Idle", "Loading", "Discharge", "Extra"].map(
        (op) => ({
            Operation: op,
            HS: data?.second_section?.Port_Consumptions?.[
                op === "Discharge" ? "Disch" : op
            ]?.HS,
            LS: data?.second_section?.Port_Consumptions?.[
                op === "Discharge" ? "Disch" : op
            ]?.LS,
            MGO: data?.second_section?.Port_Consumptions?.[
                op === "Discharge" ? "Disch" : op
            ]?.MGO,
        }),
    );

    const splitHire = data?.third_section?.split_hire || [];

    const bunker_qty = [
        {
            Operation: "On delivery",
            Main: data?.third_section?.bunker_qty?.bnkDelhs,
            ECA: data?.third_section?.bunker_qty?.bnkDells,
            MGO: data?.third_section?.bunker_qty?.bnkDelmgo,
        },
        {
            Operation: "Exp on Re-Delivery",
            Main: data?.third_section?.bunker_qty?.bnkQtyRedeliveryhs,
            ECA: data?.third_section?.bunker_qty?.bnkQtyRedeliveryls,
            MGO: data?.third_section?.bunker_qty?.bnkQtyRedeliverymgo,
        },
        {
            Operation: "Tolerance",
            Main: data?.third_section?.bunker_qty?.bnkRedeliveryQtyTolerancehs,
            ECA: data?.third_section?.bunker_qty?.bnkRedeliveryQtyTolerancels,
            MGO: data?.third_section?.bunker_qty?.bnkRedeliveryQtyTolerancemgo,
        },
    ];

    const bunker_price = [
        {
            Operation: "On delivery",
            Main: data?.third_section?.bunker_price?.bnkPriceDeliveryhs,
            ECA: data?.third_section?.bunker_price?.bnkPriceDeliveryls,
            MGO: data?.third_section?.bunker_price?.bnkPriceDeliverymgo,
        },
        {
            Operation: "On Re-Delivery",
            Main: data?.third_section?.bunker_price?.bnkPriceRedeliveryhs,
            ECA: data?.third_section?.bunker_price?.bnkPriceRedeliveryls,
            MGO: data?.third_section?.bunker_price?.bnkPriceRedeliverymgo,
        },
        {
            Operation: "For Settlement",
            Main: data?.third_section?.bunker_price?.bnkPriceSettlemenths,
            ECA: data?.third_section?.bunker_price?.bnkPriceSettlementls,
            MGO: data?.third_section?.bunker_price?.bnkPriceSettlementmgo,
        },
    ];

    const expenses = {
        "Date Opening": data?.fourth_section?.dateOpen
            ? moment(data?.fourth_section?.dateOpen).format("DD MMM YYYY • hh:mm A")
            : "-",
        "Hire Per Day": data?.fourth_section?.hirePerDay || 0,
        "Hire GBB": data?.fourth_section?.hireGbb || 0,
        "Extra Expenses": data?.fourth_section?.extraExpense || 0,
        Constants: data?.fourth_section?.constants || 0,
        GBB: data?.fourth_section?.Gbb || 0,
    };

    const expensesEntries = Object.entries(expenses);

    return (
        <View style={styles.mainContainer}>
            {/* REQUIRED HIDDEN ID */}
            <Text style={styles.hiddenIdText}>myVesselpdfId:{pdfId + "#"}</Text>

            {/* SECTION 1: VESSEL PARTICULARS */}
            <SectionTitle title="Vessel Particulars" />
            <View style={styles.card}>
                <View style={styles.roundedContainer}>
                    {vesselRows.map((row, rowIndex) => (
                        <View
                            key={rowIndex}
                            style={[
                                styles.particularsRow,
                                rowIndex === vesselRows.length - 1 && { borderBottomWidth: 0 },
                            ]}
                        >
                            {row.map((item, colIndex) => (
                                <View
                                    key={colIndex}
                                    style={[
                                        styles.particularsCellGroup,
                                        colIndex === row.length - 1 && { borderRightWidth: 0 },
                                    ]}
                                >
                                    <View style={styles.particularsLabelCell}>
                                        <Text style={styles.particularsLabelText} maxLines={1}>
                                            {item.label}
                                        </Text>
                                    </View>
                                    <View style={styles.particularsValueCell}>
                                        <Text style={styles.particularsValueText} maxLines={1}>
                                            {item.value !== undefined && item.value !== null && item.value !== ""
                                                ? String(item.value)
                                                : "-"}
                                        </Text>
                                    </View>
                                </View>
                            ))}
                        </View>
                    ))}
                </View>
            </View>

            {/* SECTION 2: CONSUMPTION PROFILES */}
            <SectionTitle title="Consumption Profiles" />
            <View style={styles.card}>
                <View style={styles.rowSplit}>
                    <View style={styles.colThird}>
                        <DataTable
                            title="Ballast Sea"
                            headers={["Speed", "HS", "LS", "MGO"]}
                            keys={["Speed", "HS", "LS", "MGO"]}
                            data={ballastConsumptionData}
                            hasCheckbox={true}
                        />
                    </View>
                    <View style={[styles.colThird, { marginLeft: 6 }]}>
                        <DataTable
                            title="Laden Sea"
                            headers={["Speed", "HS", "LS", "MGO"]}
                            keys={["Speed", "HS", "LS", "MGO"]}
                            data={ladenConsumptionData}
                            hasCheckbox={true}
                        />
                    </View>
                    <View style={[styles.colThird, { marginLeft: 6 }]}>
                        <DataTable
                            title="Port Consumption"
                            headers={["Operation", "HS", "LS", "MGO"]}
                            keys={["Operation", "HS", "LS", "MGO"]}
                            data={portConsumptionData}
                        />
                    </View>
                </View>
            </View>

            {/* SECTION 3: COMMERCIAL & BUNKER DATA */}
            <SectionTitle title="Commercial & Bunker Data" />
            <View style={styles.card}>
                <View style={[styles.rowSplit, { marginBottom: 6 }]}>
                    <View style={styles.colHalf}>
                        <DataTable
                            title="Split Hire Rates"
                            headers={["No. of Days", "Hire Rate"]}
                            keys={["NoDays", "hireRate"]}
                            data={splitHire}
                        />
                    </View>
                    <View style={[styles.colHalf, { marginLeft: 6 }]}>
                        <View style={styles.roundedContainer}>
                            <View style={styles.subSectionHeader}>
                                <Text style={styles.subSectionTitle}>Expenses & Overview</Text>
                            </View>
                            <View style={{ paddingVertical: 0 }}>
                                {expensesEntries.map(([key, value], index) => (
                                    <View
                                        key={index}
                                        style={[
                                            styles.expenseRow,
                                            index === expensesEntries.length - 1 && {
                                                borderBottomWidth: 0,
                                            },
                                        ]}
                                    >
                                        <Text style={styles.expenseLabel}>{key}:</Text>
                                        <Text style={styles.expenseValue}>{value}</Text>
                                    </View>
                                ))}
                            </View>
                        </View>
                    </View>
                </View>

                <View style={styles.rowSplit}>
                    <View style={styles.colHalf}>
                        <DataTable
                            title="Bunker Quantity"
                            headers={["Operation", "Main", "ECA", "MGO"]}
                            keys={["Operation", "Main", "ECA", "MGO"]}
                            data={bunker_qty}
                        />
                    </View>
                    <View style={[styles.colHalf, { marginLeft: 6 }]}>
                        <DataTable
                            title="Bunker Price"
                            headers={["Operation", "Main", "ECA", "MGO"]}
                            keys={["Operation", "Main", "ECA", "MGO"]}
                            data={bunker_price}
                        />
                    </View>
                </View>
            </View>
        </View>
    );
};

export default MyVesselPdfData;
